import { useCallback, useEffect, useState } from 'react';
import type { ModelShowPricing, UpdateModelShowPricingRequest } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { useAuth } from '@fetmode/app-core';

export function useModelShowPricing(): {
  pricing: ModelShowPricing | null;
  setPricing: (next: ModelShowPricing) => void;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  save: (patch: UpdateModelShowPricingRequest) => Promise<void>;
} {
  const { api } = useAuth();
  const [pricing, setPricingState] = useState<ModelShowPricing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .getModelShowPricing()
      .then((result) => {
        setPricingState(result.pricing);
      })
      .catch(() => {
        setError(t('common.studio.pricing.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [api]);

  const setPricing = useCallback((next: ModelShowPricing): void => {
    setPricingState(next);
  }, []);

  const save = useCallback(
    async (patch: UpdateModelShowPricingRequest): Promise<void> => {
      setIsSaving(true);
      setError(null);
      try {
        const result = await api.updateModelShowPricing(patch);
        setPricingState(result.pricing);
      } catch {
        setError(t('common.studio.pricing.saveError'));
      } finally {
        setIsSaving(false);
      }
    },
    [api],
  );

  return { pricing, setPricing, isLoading, isSaving, error, save };
}
